import * as React from 'react';
import {CircularProgress} from '@material-ui/core';
import Button from '@components/common/button/Button';
import {IEmbedNavigation, isSwedish} from './articleNavigation';

interface IProps {
  src: string;
  title: string;
  language: string;
  onNavigate: (navigation: IEmbedNavigation) => void;
  onRead?: (id: number) => void;
}

interface IState {
  status: 'loading' | 'ready' | 'error';
  attempt: number;
}

// The websites acknowledge a load explicitly: iframe onLoad also fires for
// a 404 or a page blocked by frame-ancestors.
export class WebsiteFrame extends React.Component<IProps, IState> {
  public state: IState = {status: 'loading', attempt: 0};
  private frame = React.createRef<HTMLIFrameElement>();
  private timeout?: number;
  private articleId?: number;
  private read = false;

  public componentDidMount() {
    window.addEventListener('message', this.receive);
    this.startTimeout();
  }

  public componentWillUnmount() {
    window.removeEventListener('message', this.receive);
    window.clearTimeout(this.timeout);
  }

  public render() {
    const swedish = isSwedish(this.props.language);
    return (
      <div style={{position: 'relative', flex: 1, minHeight: 0, height: '100%', background: '#fff'}}>
        {/* allow-forms lets the site's search submit handler run and cancel navigation. */}
        {/* Keep the iframe rendered under the loading cover: visibility toggles can break iOS touch scrolling. */}
        <iframe
          key={this.state.attempt}
          ref={this.frame}
          src={this.props.src}
          title={this.props.title}
          onLoad={this.initialize}
          onError={this.fail}
          sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox"
          referrerPolicy="no-referrer"
          aria-hidden={this.state.status !== 'ready'}
          tabIndex={this.state.status === 'ready' ? 0 : -1}
          style={{border: 0, width: '100%', height: '100%', display: 'block'}}
        />
        {this.state.status !== 'ready' && (
          <div role={this.state.status === 'error' ? 'alert' : 'status'}
            style={{position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center', gap: 20, padding: 24, textAlign: 'center', background: '#fff'}}>
            {this.state.status === 'loading' ? (
              <><CircularProgress/><span>{swedish ? 'Laddar…' : 'Loading…'}</span></>
            ) : (
              <>
                <p>{swedish ? 'Sidan kunde inte öppnas. Kontrollera anslutningen och försök igen.'
                  : 'This page could not be opened. Check your connection and try again.'}</p>
                <Button variant="outlined" onClick={this.retry}>{swedish ? 'Försök igen' : 'Try again'}</Button>
              </>
            )}
          </div>
        )}
      </div>
    );
  }

  private initialize = () => {
    this.frame.current?.contentWindow?.postMessage({type: 'co-parenting:embed:init'}, new URL(this.props.src).origin);
  };

  private receive = (event: MessageEvent) => {
    if (event.source !== this.frame.current?.contentWindow || event.origin !== new URL(this.props.src).origin) return;
    const message = event.data;
    if (!message || typeof message !== 'object') return;
    if (message.type === 'co-parenting:embed:ready') {
      window.clearTimeout(this.timeout);
      this.articleId = Number.isInteger(message.id) && message.id > 0 && message.id <= 100000 ? message.id : undefined;
      this.setState({status: 'ready'});
    } else if (message.type === 'co-parenting:embed:navigate' && this.state.status === 'ready') {
      this.props.onNavigate(message);
    } else if (message.type === 'co-parenting:embed:read' && this.state.status === 'ready'
      && this.articleId && message.id === this.articleId && !this.read) {
      this.read = true;
      if (this.props.onRead) this.props.onRead(this.articleId);
    }
  };

  private fail = () => {
    window.clearTimeout(this.timeout);
    this.setState({status: 'error'});
  };

  private startTimeout = () => {
    window.clearTimeout(this.timeout);
    this.timeout = window.setTimeout(this.fail, 20000);
  };

  private retry = () => {
    this.articleId = undefined;
    this.read = false;
    this.setState(state => ({status: 'loading', attempt: state.attempt + 1}), this.startTimeout);
  };
}



// WEBPACK FOOTER //
// ./src/containers/knowledge/WebsiteFrame.tsx